<template>
    <div>
        <report-header
            :title="title"
            :isShowDateRange="true"
            :formatDateRangeTitle="dateRange"
        />
        <b-card id="report-card">
            <div id="report-body" class="w-100">
                <QTable
                    :data="result"
                    :columns="formattedColumns"
                    :hide-bottom="true"
                    :rows-per-page-options="[0]"
                    row-key="item_id"
                    :loading="isLoading"
                    class="q-table"
                >
                    <template v-slot:bottom-row>
                        <q-tr
                            v-if="result.length == 0 && !isLoading && !isError"
                        >
                            <q-td colspan="7">No records available</q-td>
                        </q-tr>
                    </template>
                    <template v-slot:header-cell="props">
                        <q-th :props="props">
                            <strong>{{ props.col.label }}</strong>
                        </q-th>
                    </template>
                </QTable>
            </div>
        </b-card>
    </div>
</template>
<script>
import { QTable } from "quasar";
import "@/css/quasar-table.css";
import Report from "@/mixins/report.js";
import ReportService from "@/services/report";
import CREPORT from "@/constants/report";
export default {
    components: {
        QTable,
        ReportHeader: () => import("@/components/report/report-header")
    },
    mixins: [Report],
    props: {
        data: {
            type: Object,
            default() {
                return {
                    start: this.getStartDate("day"),
                    end: this.getStartDate("day"),
                    datePickerOptions: {
                        format: this.datepickerFormat
                    },
                    ignoreFormat: 1
                };
            }
        }
    },
    data() {
        return {
            locale: "en-US",
            meta: "",
            params: "",
            code: "",
            result: [],
            renderedDateTime: moment().format("lll"),
            isLoading: true,
            isError: false,
            dateRange: "",
            title: "Sales Tax Summary"
        };
    },
    mounted() {
        let self = this;
        self.data.isShowDateRange = true;

        this._getSetting().then(() => {
            self.data.start = self.getStartDate("day");
            self.data.end = self.getEndDate("day");
            self.data.from = self.converToUnix(self.data.start);
            self.data.to = self.converToUnix(self.data.end);
            self.init();
        });

        self.data.datePickerOptions.format = self.datepickerFormat;

        self.$root.$on("report-filter-date-range:refresh", this.init);
    },
    beforeDestroy: function () {
        this.$root.$off("report-filter-date-range:refresh", this.init);
    },
    methods: {
        async init(params) {
            let self = this;
            self.data.ignoreFormat = 1;
            self.isLoading = true;
            self.isError = false;
            if (params) {
                this.data.from = params.from;
                this.data.to = params.to;
                this.data.start = params.start;
                this.data.end = params.end;
            }
            self.dateRange = self.getDateRange(self.data.from, self.data.to);

            await ReportService.getReport(CREPORT.TYPE.SALES_TAX_SUMMARY, {
                from: params ? params.from : self.data.start.unix(),
                to: params ? params.to : self.data.end.unix()
            })
                .then(res => {
                    if (res && res.message && res.message.code === 200) {
                        self.meta = res.resource.meta;
                        self.resource = res.resource.results;
                        self.locale = self.meta.locale.replace("_", "-");
                        self.code = res.message.code;
                        self.params = {
                            results: res.resource.results
                        };
                        self.dynamicPageTitle(self.title);
                        if (self.code === 200) {
                            self.result = self.params.results;
                            self.dateRange = self.getDateRange(
                                res.resource.meta.from,
                                res.resource.meta.to
                            );
                            self.renderedDateTime = moment()
                                .tz(self.meta.timezone)
                                .format("lll");
                        }
                    } else {
                        throw new Error("Error: Unable to get the report.");
                    }
                })
                .catch(e => {
                    console.error(e);
                    self.isLoading = false;
                    self.isError = true;
                    self._message("Error: Unable to get the report", "danger");
                })
                .finally(() => {
                    self.isLoading = false;
                });
        }
    },
    computed: {
        formattedColumns() {
            return [
                {
                    name: "jurisdiction",
                    label: "Jurisdiction",
                    field: "tax_name",
                    align: "left",
                    style: "white-space: nowrap"
                },
                {
                    name: "taxable",
                    label: "Taxable Sales",
                    align: "center",
                    style: "white-space: nowrap",
                    field: row => row.taxable,
                    format: (val, row) => {
                        return this._toCurrency(row.taxable);
                    }
                },
                {
                    name: "nontaxable",
                    label: "Non-Taxable Sales",
                    field: row => row.nontaxable,
                    format: (val, row) => {
                        return this._toCurrency(row.nontaxable);
                    },
                    align: "center",
                    style: "white-space: nowrap"
                },
                {
                    name: "total_sales_ex_tax",
                    label: "Total Sales (Ex. Tax)",
                    field: row => row.total_sales_ex_tax,
                    format: (val, row) => {
                        return this._toCurrency(row.total_sales_ex_tax);
                    },
                    align: "center",
                    style: "white-space: nowrap"
                },
                {
                    name: "tax_total",
                    label: "Tax Collected",
                    field: row => row.tax_total,
                    format: (val, row) => {
                        return this._toCurrency(row.tax_total);
                    },
                    align: "center",
                    style: "white-space: nowrap"
                },
                {
                    name: "total_sales_inc_tax",
                    label: "Total Sales (Inc. Tax)",
                    field: row => row.total_sales_inc_tax,
                    format: (val, row) => {
                        return this._toCurrency(row.total_sales_inc_tax);
                    },
                    align: "center",
                    style: "white-space: nowrap"
                },
                {
                    name: "cogs",
                    label: "Avg. COGS",
                    field: row => row.cogs,
                    format: (val, row) => {
                        return this._toCurrency(row.cogs);
                    },
                    align: "center",
                    style: "white-space: nowrap"
                }
            ];
        }
    }
};
</script>

<style scoped>
p.item-name {
    font-weight: bold;
    font-size: 1.2em;
    margin: 20px 0 0 0;
    padding: 5px 15px;
    border-bottom: 1px solid #c6c6c6;
}
</style>
